// src/guidance/templates/cloneDefinition.ts
import { nanoid } from 'nanoid/non-secure';
import type { AppDefinition } from '@/types/app';
import type { ComponentDefinition } from '@/types/component';

// Deep-clone a template definition with fresh ids so each created project is
// independent. Screen ids are remapped and navigation screenIds updated to match;
// component ids (including nested children and renderItem) are regenerated.
export function cloneDefinitionWithIds(def: AppDefinition): AppDefinition {
  const clone: AppDefinition = JSON.parse(JSON.stringify(def));
  clone.id = nanoid();

  const screenIdMap = new Map<string, string>();
  for (const screen of clone.screens) {
    const newId = nanoid();
    screenIdMap.set(screen.id, newId);
    screen.id = newId;
    screen.components = screen.components.map(regenComponentIds);
  }

  if (clone.navigation?.screens) {
    for (const navScreen of clone.navigation.screens) {
      const mapped = screenIdMap.get(navScreen.screenId);
      if (mapped) navScreen.screenId = mapped;
    }
    if (clone.navigation.initialScreen) {
      clone.navigation.initialScreen = screenIdMap.get(clone.navigation.initialScreen) ?? clone.navigation.initialScreen;
    }
  }
  return clone;
}

function regenComponentIds(comp: ComponentDefinition): ComponentDefinition {
  comp.id = nanoid();
  if (comp.children) comp.children = comp.children.map(regenComponentIds);
  const ri = (comp.props as Record<string, unknown> | undefined)?.renderItem as ComponentDefinition | undefined;
  if (ri && typeof ri === 'object' && 'type' in ri) {
    (comp.props as Record<string, unknown>).renderItem = regenComponentIds(ri);
  }
  const empty = (comp.props as Record<string, unknown> | undefined)?.emptyComponent as ComponentDefinition | undefined;
  if (empty && typeof empty === 'object' && 'type' in empty) {
    (comp.props as Record<string, unknown>).emptyComponent = regenComponentIds(empty);
  }
  return comp;
}
